import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import MutationQueueLink from "@adobe/apollo-link-mutation-queue";
import { ApolloLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { RetryLink } from "@apollo/client/link/retry";
import React from "react";
import ReactDOM from "react-dom";
import { browserTracingIntegration, init, Replay } from "@sentry/react";
import "./index.scss";
import { Adapter } from "@magento/venia-drivers";
import store from "./store";
import app from "@magento/peregrine/lib/store/actions/app";
import App from "./App";
import AppContextProvider from "./contextProvider";
import BrowserPersistence from "@overrides_venia/peregrine/util/simplePersistence";
import { registerSW } from "./registerSW";
import { env } from "build/env";
import { isPrivateTokenPath, scrubBreadcrumb, scrubSentryEvent, scrubSentryTransaction, scrubTokenUrl } from "./utils/scrubTokens";
const apiBase = process.env.GRAPHQL_ENDPOINT;
init({
  dsn: process.env.SENTRY_DSN,
  environment: process.env.SENTRY_ENVIRONMENT,
  release: process.env.SENTRY_RELEASE,
  integrations: [browserTracingIntegration({
    beforeStartSpan: context => {
      let path = location.pathname;
      if (path.endsWith('/') && path.length > 1) {
        path = path.slice(0, -1);
      }
      if (path.startsWith('/it/') || path.startsWith('/en/')) {
        path = path.substring(3);
      } else if (path === '/it' || path === '/en') {
        path = '/';
      }
      return _objectSpread(_objectSpread({}, context), {}, {
        name: scrubTokenUrl(path)
      });
    }
  }),
  // Replay is not recorded on the token pages (the URL is the credential)
  ...(isPrivateTokenPath(window.location.pathname) ? [] : [new Replay({
    maskAllText: false,
    blockAllMedia: false,
    networkDetailAllowUrls: [window.location.origin, process.env.CHECKPLEASE_V2_SERVER_URL, process.env.GRAPHQL_ENDPOINT, process.env.EXPONEA_TARGET + "/bulk"]
  })])],
  // Performance Monitoring
  tracesSampleRate: parseFloat(process.env.SENTRY_TRACES_SAMPLE_RATE),
  tracePropagationTargets: [process.env.CHECKPLEASE_V2_SERVER_URL],
  // Session Replay
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1,
  beforeBreadcrumb: scrubBreadcrumb,
  beforeSendTransaction: scrubSentryTransaction,
  beforeSend: (event, hint) => {
    scrubSentryEvent(event);
    const handled = event.level !== "fatal";
    // Safety check for optional chaining
    if (event.exception && event.exception.values) {
      event.exception.values.forEach(value => {
        if (value.mechanism) {
          value.mechanism.handled = handled;
        } else {
          value.mechanism = {
            type: 'generic',
            handled: handled
          };
        }
      });
    }
    return event;
  },
  // REFACTOR: Cleaned up "Cargo Cult" ignores. 
  // Removed ancient iOS/Facebook/Extension errors that no longer exist.
  ignoreErrors: ['Failed to fetch', 'NetworkError when attempting to fetch resource.', 'ResizeObserver loop limit exceeded' // Benign browser warning
  ],
  ignoreUrls: [
  // Keep strict extension ignoring
  /extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],
  denyUrls: [/\/cdn-cgi\/zaraz\/s/i]
});

// --- APOLLO CONFIGURATION ---

const authLink = setContext((_, _ref) => {
  let {
    headers
  } = _ref;
  const storage = new BrowserPersistence();
  const token = storage.getItem("signin_token");
  const language = storage.getItem("language_code") || "it";
  const newHeaders = _objectSpread({
    Store: env.store_view_code,
    userLanguage: language
  }, headers);
  if (token) {
    newHeaders.authorization = "Bearer ".concat(token);
  }
  return {
    headers: newHeaders
  };
});
const link = new RetryLink({
  delay: {
    initial: 300,
    max: 10000,
    jitter: true
  },
  attempts: {
    max: 5,
    retryIf: (error, _operation) => !!error
  }
}, {
  name: "retry-all-operations"
});
const apolloLink = ApolloLink.from([new MutationQueueLink(), link, authLink, Adapter.apolloLink(apiBase)]);

// --- MOUNTING LOGIC (THE FIX) ---

const MOUNT_NODE_ID = "root";

/**
 * Safely mounts the React application.
 * Handles race conditions where the DOM might not be ready.
 */
const mountApplication = function () {
  let retryCount = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  if (window.self !== window.top) {
    return;
  }
  const container = document.getElementById(MOUNT_NODE_ID);
  const MAX_RETRIES = 50; // 50 * 20ms = 1 second max wait

  if (!container) {
    if (retryCount < MAX_RETRIES) {
      // If specific browser update slowed down DOM parsing, this catches it.
      setTimeout(() => mountApplication(retryCount + 1), 20);
      return;
    }

    // If we still fail, log a fatal error to Sentry manually so we know it's happening
    console.error("FATAL: Failed to mount app. Element #".concat(MOUNT_NODE_ID, " not found after retries."));
    return;
  }
  ReactDOM.render(/*#__PURE__*/React.createElement(Adapter, {
    apiBase: apiBase,
    apollo: {
      link: apolloLink
    },
    store: store
  }, /*#__PURE__*/React.createElement(AppContextProvider, null, /*#__PURE__*/React.createElement(App, null))), container);
};

// Ensure we don't run until the document is actually parsed
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => mountApplication());
} else {
  mountApplication();
}

// --- POST-MOUNT LOGIC ---

registerSW();
window.addEventListener("online", () => {
  store.dispatch(app.setOnline());
});
window.addEventListener("offline", () => {
  store.dispatch(app.setOffline());
});
if (module.hot) {
  module.hot.accept();
}